import React, { useState, useContext } from 'react';
import {
  TabbedView,
  Tab,
  TabList,
  TabPanel,
} from 'components/shared/TabbedView';
import {
  FilterContext,
  FilterProvider,
} from 'components/shared/FilterContext/index';
import { BlankSlate, Description } from 'components/_shared/blank_slate';
import noResultsSrc from 'images/content_no_results.svg';

import AnalyzerRightsContents from 'components/AnalyzerRightsContents';
import FeatureFlag from 'components/shared/FeatureFlag';
import RatingsModule from './Ratings/RatingsModule';
import LearnerEngagementContainer from './LearnerEngagementContainer';
import ActivityFeedContainer from './ActivityFeed/ActivityFeedContainer';
import LearnerImpactContainer from './LearnerImpact/LearnerImpactContainer';
import MoreReportsContainer from './MoreReports/MoreReportsContainer';
import Filters from './Filters';
import V2Banner from './v2/V2Banner';

const HiddenWithFilters = ({ children }) => {
  const { params: filters } = useContext(FilterContext);
  const hasDateFilter = filters.start_date || filters.end_date;
  const hasGroupsFilter = filters.group_ids;
  const description = t('insights.filters.hidden_tab_with_filters');
  if (hasDateFilter || hasGroupsFilter) {
    return (
      <BlankSlate>
        <img className="mw-300 mb-32" src={noResultsSrc} alt={description} />
        <Description>{description}</Description>
      </BlankSlate>
    );
  }
  return children;
};

const InsightsRoot = ({
  i18n,
  meta: { permissions, features, groups },
  filters,
}) => {
  const [tabIndex, setTabIndex] = useState(0);
  return (
    <FilterProvider initialState={filters} toParams={Filters.toParams}>
      <div className="mb-128">
        {features.embeddedInsightsPage && <V2Banner isOldPage />}
        <header className="Index-header mt-48 mb-24 ff-ss">
          <h1 className="Index-title">
            <FeatureFlag
              features="readiness-enable-unification"
              fallback={i18n.title}
            />
          </h1>
        </header>
        <Filters groups={groups} manageAllUsers={permissions.manageAllUsers} />
        <TabbedView onSelect={setTabIndex} selectedIndex={tabIndex}>
          <TabList className="mb-24" tabClassName="c-brand-night">
            <Tab>{i18n.engagement.title}</Tab>
            {permissions.lessonRatings && <Tab>{i18n.ratings.title}</Tab>}
            {permissions.learnerImpact && (
              <Tab>{t('insights.learner_impact.title')}</Tab>
            )}
            {permissions.latestActivities && (
              <Tab>{i18n.activity_feed.title}</Tab>
            )}
            <Tab>{t('training_activity.name')}</Tab>
            <Tab>{t('insights.more_reports.title')}</Tab>
          </TabList>
          <TabPanel>
            <LearnerEngagementContainer i18n={i18n} permissions={permissions} />
          </TabPanel>
          {permissions.lessonRatings && (
            <TabPanel>
              <RatingsModule i18n={i18n} />
            </TabPanel>
          )}
          {permissions.learnerImpact && (
            <TabPanel>
              <LearnerImpactContainer />
            </TabPanel>
          )}
          {permissions.latestActivities && (
            <TabPanel>
              <ActivityFeedContainer i18n={i18n} />
            </TabPanel>
          )}
          <TabPanel>
            <HiddenWithFilters>
              <AnalyzerRightsContents />
            </HiddenWithFilters>
          </TabPanel>

          <TabPanel>
            <MoreReportsContainer permissions={permissions} />
          </TabPanel>
        </TabbedView>
      </div>
    </FilterProvider>
  );
};

// Wrapping the InsightsRoot component in a another function as a workaround for
// ReactOnRails in order to get hooks to work, otherwise, they they throw an error.
export default ({ ...props }) => <InsightsRoot {...props} />;
